import {
  BarChart3, Bot, Building2, CreditCard, FileBarChart, Gauge, GitBranch, Globe, KeyRound, Layers, LayoutDashboard, MessageSquare,
  Palette, Plug, Radio, Receipt, Settings, ShieldCheck, Target, Users, Video, Workflow, Activity, ScrollText, FileInput, UsersRound, Megaphone, Calculator, Mail, Webhook, Handshake, Sparkles, Cog, Filter, SlidersHorizontal, Code2, LifeBuoy,
  type LucideIcon,
} from "lucide-react";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: string;
  /** Só marca ativo em correspondência exata (para itens com subrotas irmãs, ex.: Integrações vs Meta Ads). */
  end?: boolean;
  /** Exibe se o usuário tiver QUALQUER uma destas permissões (item que abre uma página com abas). */
  anyPermission?: string[];
  /** Prefixo(s) de rota que também marcam o item como ativo (ex.: abas irmãs em outros endereços). */
  match?: string | string[];
}

export interface SectionTab {
  to: string;
  label: string;
  permission: string;
  end?: boolean;
}

/** Abas de Rastreamento. */
export const TRACKING_TABS: SectionTab[] = [
  { to: "/app/tracking/sites", label: "Pixel e sites", permission: "tracking:manage" },
  { to: "/app/tracking/links", label: "Links", permission: "tracking:manage" },
  { to: "/app/tracking/events", label: "Eventos", permission: "tracking:manage" },
];

export interface NavGroup {
  /** Identificador estável do módulo (submenu recolhível). Ausente = grupo achatado (itens no topo). */
  id?: string;
  label?: string;
  /** Ícone do módulo no cabeçalho do submenu. */
  icon?: LucideIcon;
  items: NavItem[];
  /** Linha separadora acima do grupo (sem título). */
  divider?: boolean;
}

// Menu do painel (03/11): seções fixas, sem submenus recolhíveis. Telas irmãs viram abas dentro da página
// (Rastreamento: Sites/Links/Eventos). Configurações é um submenu (04/11) com as telas de configuração, Guia, Assinatura e Partner.
export const APP_NAV: NavGroup[] = [
  {
    items: [
      { to: "/app/overview", label: "Visão geral", icon: LayoutDashboard, permission: "analytics:read" },
      // Consolidado multi-workspace: a API exige vínculo de organização; usuários sem escopo recebem erro de autorização.
      { to: "/app/organization", label: "Visão da organização", icon: Building2, permission: "analytics:read" },
      { to: "/app/leads", label: "Leads", icon: UsersRound, permission: "leads:read" },
      { to: "/app/opportunities", label: "Oportunidades", icon: Layers, permission: "leads:read" },
      { to: "/app/pipelines", label: "Pipelines", icon: Filter, permission: "leads:read" },
      { to: "/app/revenue", label: "Receita", icon: Receipt, permission: "revenue:read" },
    ],
  },
  {
    label: "Marketing",
    items: [
      { to: "/app/acquisition", label: "Campanhas", icon: Target, permission: "analytics:read" },
      { to: "/app/creatives", label: "Criativos", icon: Video, permission: "analytics:read" },
      { to: "/app/attribution", label: "Atribuição", icon: GitBranch, permission: "analytics:read" },
      { to: "/app/planning", label: "Planejamento", icon: Calculator, permission: "analytics:read" },
      { to: "/app/reports", label: "Relatórios", icon: FileBarChart, permission: "analytics:read" },
    ],
  },
  {
    label: "Ferramentas",
    items: [
      { to: "/app/integrations/meta", label: "Meta Ads", icon: Megaphone, permission: "integrations:manage" },
      { to: "/app/integrations", label: "Integrações", icon: Plug, permission: "integrations:manage", end: true },
      { to: "/app/automations", label: "Automações", icon: Workflow, permission: "automations:manage" },
      { to: "/app/forms", label: "Formulários", icon: FileInput, permission: "integrations:manage" },
      { to: "/app/audiences", label: "Públicos", icon: Users, permission: "audiences:manage" },
      { to: "/app/tracking/sites", label: "Rastreamento", icon: Radio, permission: "tracking:manage", match: "/app/tracking" },
    ],
  },
  {
    divider: true,
    items: [{ to: "/app/feedback", label: "Suporte e sugestões", icon: MessageSquare }],
  },
  {
    id: "config",
    label: "Configurações",
    icon: Settings,
    items: [
      { to: "/app/guide", label: "Guia de configuração", icon: Sparkles },
      { to: "/app/settings", label: "Geral", icon: Cog, permission: "settings:manage", end: true },
      { to: "/app/team", label: "Equipe", icon: Users, permission: "team:manage" },
      { to: "/app/settings/workspaces", label: "Workspaces", icon: Building2, permission: "workspaces:manage" },
      { to: "/app/settings/custom-fields", label: "Campos personalizados", icon: SlidersHorizontal, permission: "settings:manage" },
      { to: "/app/settings/inside-sales", label: "Painel de vendas", icon: Gauge, permission: "settings:manage" },
      { to: "/app/developers", label: "Desenvolvedores", icon: Code2, permission: "developers:manage" },
      { to: "/app/settings/connected-apps", label: "Apps conectados", icon: Plug },
      { to: "/app/settings/support", label: "Acesso de suporte", icon: LifeBuoy, permission: "support:grant" },
      { to: "/app/billing", label: "Assinatura", icon: CreditCard, permission: "billing:read" },
      { to: "/app/partner", label: "Partner", icon: Handshake, permission: "billing:read" },
    ],
  },
];

export const ADMIN_NAV: NavGroup[] = [
  {
    items: [
      { to: "/admin/overview", label: "Visão geral", icon: Gauge, permission: "brand:read" },
      { to: "/admin/organizations", label: "Organizações", icon: Building2, permission: "brand:read" },
      { to: "/admin/plans", label: "Planos e preços", icon: Receipt, permission: "brand:billing" },
      { to: "/admin/billing", label: "Cobrança", icon: CreditCard, permission: "brand:billing" },
      { to: "/admin/partners", label: "Partners", icon: Handshake, permission: "brand:billing" },
      { to: "/admin/feedback", label: "Chamados", icon: MessageSquare, permission: "brand:read" },
      { to: "/admin/ai", label: "IA e créditos", icon: Sparkles, permission: "brand:read" },
      { to: "/admin/branding", label: "Marca e domínio", icon: Palette, permission: "brand:manage" },
      { to: "/admin/email", label: "E-mail", icon: Mail, permission: "brand:manage" },
      { to: "/admin/team", label: "Equipe", icon: Users, permission: "brand:team" },
    ],
  },
];

export const MASTER_NAV: NavGroup[] = [
  {
    items: [
      { to: "/master/overview", label: "Visão geral", icon: BarChart3 },
      { to: "/master/brands", label: "Marcas", icon: Globe },
      { to: "/master/usage", label: "Uso do sistema", icon: Gauge },
      { to: "/master/billing", label: "Cobrança atacado", icon: CreditCard },
      { to: "/master/whatsapp", label: "WhatsApp (QR)", icon: MessageSquare },
      { to: "/master/meta-webhook", label: "Webhook da Meta", icon: Webhook },
      { to: "/master/oauth", label: "Apps OAuth", icon: KeyRound },
      { to: "/master/operations", label: "Operações", icon: Activity },
      { to: "/master/audit", label: "Auditoria e suporte", icon: ScrollText },
    ],
  },
];

export { Bot, KeyRound, ShieldCheck };
